import React, { useEffect, useState } from 'react';
import { map } from 'lodash';
import { getTranslatedKPINameV2, translate } from 'helpers/translateHelper';
import colors from 'theme/_colors.scss';
import { renderToStaticMarkup } from 'react-dom/server';
import { TrendTooltip } from 'sharedComponents/ChartComponents/Trend';
import { ChartNoDataText } from 'sharedComponents/ChartComponents';
import { defaultBoostThreshold } from 'helpers/constants';
import { getMeasurementUnitSymbol } from 'helpers/unitHelper';
import { calculateDynamicChartHeight } from '../../../helpers';
import { chartHeightConstants } from '../../../constants';
import {
    CHART_CONDITION_TREND_KPI_TYPE,
    CHART_CONDITION_TREND_KPI_SIGNAL_TYPE,
    CHART_CONDITION_TREND_KPI_CALCULATED_SIGNAL_TYPE,
    statusBarColors
} from './constants';
import { getXAxisLabelDateFormatForDriveAnomaly } from '../helpers';

const PRINT_FONT_FAMILY = 'ABBvoice-Regular';

const STATUS_BAR_LAYOUT = {
    signalAreaHeightPercent: 86,
    statusBarTopPercent: 94,
    statusBarHeightPercent: 6,
    minTitleGapPx: 8,
    maxTitleGapPx: 24,
    titleX: 12,
};

const renderOverallStatusLabel = (chart) => {
    const labelText = translate('ABB.Powertrain.Frontend.anomalyOverallStatusBarLabel');
    const signalBottomY = chart.plotTop + STATUS_BAR_LAYOUT.signalAreaHeightPercent / 100 * chart.plotHeight;
    const statusTopY = chart.plotTop + STATUS_BAR_LAYOUT.statusBarTopPercent / 100 * chart.plotHeight;
    const safeTopY = signalBottomY + STATUS_BAR_LAYOUT.minTitleGapPx;
    const safeBottomY = statusTopY - STATUS_BAR_LAYOUT.minTitleGapPx;
    const targetCenterY = safeTopY <= safeBottomY
        ? (safeTopY + safeBottomY) / 2
        : statusTopY - STATUS_BAR_LAYOUT.minTitleGapPx;
    const targetX = chart.plotLeft + STATUS_BAR_LAYOUT.titleX;

    if (!chart._overallStatusLabel) {
        chart._overallStatusLabel = chart.renderer
            .text(labelText, targetX, targetCenterY)
            .css({
                fontFamily: PRINT_FONT_FAMILY,
                fontWeight: 400,
                fontSize: '12px',
                color: colors.tundora,
            })
            .attr({ zIndex: 7 })
            .add();
    }

    chart._overallStatusLabel.attr({ text: labelText, x: targetX });
    const bbox = chart._overallStatusLabel.getBBox();
    chart._overallStatusLabel.attr({ y: targetCenterY + bbox.height * 0.35 });
};

const getPrintableTitle = (assetDetails, chartTitle) => {
    return renderToStaticMarkup(
        <div className='asset-title-chart' id='energy-anomaly-chart-title'>
            <span>{`${assetDetails?.details?.assetName} - ${chartTitle}`}</span>
        </div>
    );
};

const getStatusBarData = (kpiItem) => {
    return map(kpiItem?.values, (ul) => {
        const ts = new Date(ul?.timestamp).getTime();
        const hourStart = ts - ts % (60 * 60 * 1000);
        return {
            y: 1,
            x: hourStart,
            color: statusBarColors[ul?.value],
        };
    });
};

const getChartConfig = (title, assetDetails) => {
    return {
        title: { text: null },
        chart: {
            events: {
                render: function () {
                    renderOverallStatusLabel(this);
                    if (this._renderEventDisabled) { return; }
                },
                beforePrint: function () {
                    const printableTitle = getPrintableTitle(assetDetails, title);
                    if (this.exportSVGElements?.length > 0) {
                        this.exportSVGElements[0]?.hide();
                    }
                    this._renderEventDisabled = true;
                    this.setTitle({ text: printableTitle, align: 'left', x: 50, y: -10 });

                    const legendElement = this.legend?.group?.element;
                    if (this.xAxis?.length > 0) {
                        const xAxisLabels = this.xAxis[0]?.labelGroup?.element;
                        if (xAxisLabels?.style) { xAxisLabels.style.fontFamily = PRINT_FONT_FAMILY; }
                    }
                    this.yAxis?.forEach(axis => {
                        const yAxisLabels = axis.labelGroup?.element;
                        if (yAxisLabels?.style) { yAxisLabels.style.fontFamily = PRINT_FONT_FAMILY; }
                    });
                    if (legendElement?.style) { legendElement.style.fontFamily = PRINT_FONT_FAMILY; }
                },
                afterPrint: function () {
                    if (this.exportSVGElements?.length > 0) { this.exportSVGElements[0]?.show(); }
                    this.setTitle({ text: '' });
                    this._renderEventDisabled = false;
                }
            },
            style: { fontFamily: PRINT_FONT_FAMILY },
            spacingTop: 70,
            spacingRight: 25,
        },
        boost: { allowForce: false },
        time: { useUTC: false },
        enableNoDataZoom: true,
        navigator: { enabled: true },
        xAxis: {
            type: 'datetime',
            id: 'x',
            dateTimeLabelFormats: {
                millisecond: '%H:%M:%S',
                day: '%e. %b',
                month: '%b \'%y'
            },
            crosshair: { enabled: true, width: 2, color: colors.tundora },
            labels: {
                rotation: 50,
                formatter: function () {
                    return getXAxisLabelDateFormatForDriveAnomaly(this.value);
                },
                color: colors.black,
                style: { fontWeight: 'bold', color: colors.black },
            },
            lineColor: colors.black,
            lineWidth: 2,
            tickPixelInterval: 25,
            tickColor: colors.black,
            tickWidth: 1,
        },
        // energy anomaly has a single always-visible series
        legend: { enabled: false },
        credits: { enabled: false },
        tooltip: {
            shared: true,
            valueDecimals: 2,
            useHTML: true,
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            shadow: false,
            padding: 0,
            borderWidth: 0,
            dateTimeLabelFormats: {
                day: '%d.%m.%Y<br/>%H:%M',
                hour: '%d.%m %H:%M',
            },
            positioner: function (labelWidth, labelHeight, point) {
                const chart = this.chart;
                let x = point.plotX + chart.plotLeft;
                let y = point.plotY + chart.plotTop - labelHeight - 16;
                x = Math.min(x, chart.plotLeft + chart.plotWidth - labelWidth);
                y = Math.max(chart.plotTop, y);
                return { x, y };
            },
            formatter: function () {
                return renderToStaticMarkup(
                    <TrendTooltip
                        points={this.points?.filter(p =>
                            p?.series?.name !== CHART_CONDITION_TREND_KPI_TYPE &&
                            p?.series?.name !== CHART_CONDITION_TREND_KPI_CALCULATED_SIGNAL_TYPE
                        )}
                        timeStamp={this.x}
                        isUTC={false}
                        anomalyLevel={null}
                    />
                );
            },
        },
        plotOptions: {
            series: {
                turboThreshold: 0,
                animation: false,
                boostThreshold: 1000,
            },
            column: {
                boostThreshold: 1000,
                groupPadding: 0,
                pointPadding: 0,
                borderWidth: 0,
                pointPlacement: 0.5,
            }
        },
        navigation: {
            buttonOptions: { align: 'right' },
            menuStyle: {
                borderColor: '#fff',
                boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.08)',
                borderRadius: '6px'
            },
            menuItemStyle: { padding: '6px 12px', fontSize: '14px', fontFamily: PRINT_FONT_FAMILY },
            menuItemHoverStyle: { backgroundColor: '#ebebeb', color: '#1f1f1f' }
        },
        lang: {
            printChart: translate('ABB.Powertrain.Frontend.printChart'),
            downloadPNG: translate('ABB.Powertrain.Frontend.saveAsImage'),
            noData: renderToStaticMarkup(
                <ChartNoDataText
                    title={translate('ABB.Powertrain.Frontend.chartNoMeasurementData')}
                    text={translate('ABB.Powertrain.Frontend.chartNoMeasurementDataUserGuide')}
                />
            )
        },
        noData: { useHTML: true },
        exporting: {
            enabled: true,
            scale: 1.5,
            sourceWidth: 1100,
            sourceHeight: 500,
            fallbackToExportServer: false,
            allowHTML: true,
            printMaxWidth: 1100,
            buttons: {
                contextButton: {
                    enabled: false,
                    menuItems: ['downloadPNG', 'printChart'],
                    verticalAlign: 'top',
                    x: 0,
                    y: -70
                }
            },
            chartOptions: {
                title: {
                    text: `${assetDetails?.details?.assetName} - ${title}`,
                    align: 'left', x: 50, y: -10,
                    style: { fontFamily: 'Verdana, sans-serif', fontWeight: 'bold' }
                },
                xAxis: { labels: { style: { fontFamily: 'Verdana, sans-serif' } } },
                yAxis: { labels: { style: { fontFamily: 'Verdana, sans-serif' } } }
            }
        }
    };
};

const getSeries = (conditionTrendsData, chartInstance, energyAnomalyKPI) => {
    const seriesConfig = { yAxis: [], seriesData: [] };

    const kpiItem = conditionTrendsData?.find(item => item?.type === CHART_CONDITION_TREND_KPI_TYPE);
    const kpiSignalItem = conditionTrendsData?.find(item => item?.type === CHART_CONDITION_TREND_KPI_SIGNAL_TYPE);
    const kpiCalculatedSignalItem = conditionTrendsData?.find(item => item?.type === CHART_CONDITION_TREND_KPI_CALCULATED_SIGNAL_TYPE);
    let powerTrendYAxisId = null;

    // ── Overall anomaly status bar (Kpi type)
    if (kpiItem) {
        const statusBarYAxisId = 'energy_anomaly_status_bar_yaxis';

        seriesConfig.yAxis.push({
            top: `${STATUS_BAR_LAYOUT.statusBarTopPercent}%`,
            height: `${STATUS_BAR_LAYOUT.statusBarHeightPercent}%`,
            id: statusBarYAxisId,
            title: {
                text: null,
                enabled: false,
            },
            labels: { enabled: false },
            gridLineWidth: 0,
            lineWidth: 0,
            tickLength: 0,
        });

        seriesConfig.seriesData.push({
            name: CHART_CONDITION_TREND_KPI_TYPE,
            type: 'column',
            id: `energy_status_bar_${kpiItem?.key || 'status'}`,
            yAxis: statusBarYAxisId,
            xAxis: 'x',
            data: getStatusBarData(kpiItem),
            showInLegend: false,
            enableMouseTracking: false,
            pointRange: 60 * 60 * 1000,
            boostThreshold: 9999999,
            states: { inactive: { enabled: false }, hover: { enabled: false } }
        });
    }

    if (kpiSignalItem?.values?.length) {
        const unit = energyAnomalyKPI?.unit || kpiSignalItem?.unit || '';
        const convertedUnit = getMeasurementUnitSymbol(energyAnomalyKPI?.unitGroupId || kpiSignalItem?.unitGroupId);
        const displayUnit = convertedUnit || unit;
        const name = getTranslatedKPINameV2(energyAnomalyKPI);
        const yAxisId = `y_${kpiSignalItem?.key || 'energy_anomaly_power'}`;
        const seriesId = kpiSignalItem?.key || 'energy_anomaly_power';
        const axisTitle = displayUnit ? `${name} (${displayUnit})` : name;
        powerTrendYAxisId = yAxisId;

        const trendDataPoints = kpiSignalItem?.values?.map(point => ({
            x: new Date(point?.timestamp).getTime(),
            y: point?.value,
        }));

        seriesConfig.yAxis.push({
            top: '0%',
            height: `${STATUS_BAR_LAYOUT.signalAreaHeightPercent}%`,
            labels: { format: '{value}' },
            id: yAxisId,
            title: { text: axisTitle, enabled: true },
            visible: true,
        });

        // Main hourly power trend line with dot markers on each point
        seriesConfig.seriesData.push({
            name: axisTitle,
            type: 'line',
            data: trendDataPoints,
            tooltip: {
                valueSuffix: displayUnit ? ` ${displayUnit}` : '',
                valueDecimals: 2,
            },
            showInNavigator: true,
            visible: true,
            showInLegend: false,
            lineWidth: 1,
            marker: { enabled: true, radius: 2, symbol: 'circle', lineWidth: 0 },
            id: seriesId,
            yAxis: yAxisId,
            xAxis: 'x',
            enableMouseTracking: true
        });

    }
    if (kpiCalculatedSignalItem?.values?.length && kpiSignalItem?.values?.length && powerTrendYAxisId) {
        const powerByTimestamp = new Map(
            kpiSignalItem.values
                .filter(p => p?.value !== null && p?.value !== undefined)
                .map(p => [new Date(p.timestamp).getTime(), p?.value])
        );


        const anomalyPoints = kpiCalculatedSignalItem?.values
            ?.filter(p => p?.value === 1)
            .reduce((acc, p) => {
                const x = new Date(p.timestamp).getTime();
                const y = powerByTimestamp.get(x);
                if (y !== null && y !== undefined) { acc.push({ x, y }); }
                return acc;
            }, []);

        if (anomalyPoints?.length > 0) {
            seriesConfig.seriesData.push({
                name: CHART_CONDITION_TREND_KPI_CALCULATED_SIGNAL_TYPE,
                type: 'scatter',
                id: 'energy_anomaly_calculated_points',
                yAxis: powerTrendYAxisId,
                xAxis: 'x',
                data: anomalyPoints,
                marker: {
                    enabled: true,
                    radius: 2,
                    symbol: 'circle',
                    fillColor: colors.orangePeel,
                    lineWidth: 0,
                },
                showInLegend: false,
                showInNavigator: false,
                enableMouseTracking: false,
                states: { inactive: { enabled: false } },
                boostThreshold: 9999999,
            });
        }
    }

    const allSeriesLength = seriesConfig.seriesData.reduce(
        (count, s) => (s.data?.length || 0) + count, 0
    );

    return { ...seriesConfig, allSeriesLength };
};

const useChartConfig = (
    energyAnomalyKPI,
    title,
    assetDetails,
    conditionTrendsData,
    containerRef,
    chartInstance,
    chartHeight
) => {
    const [options, setOptions] = useState({});
    const [navigatorExtremes, setNavigatorExtremes] = useState(null);

    const chartConfig = getChartConfig(title, assetDetails);

    // Persist navigator zoom when extremes change
    useEffect(() => {
        if (chartInstance?.xAxis?.length > 0) {
            chartInstance.xAxis[0]?.update({
                ...navigatorExtremes && { min: navigatorExtremes?.min, max: navigatorExtremes?.max }
            });
        }
    }, [navigatorExtremes, chartInstance]);

    useEffect(() => {
        if (!conditionTrendsData?.length) { return; }
        const containerHeight = containerRef?.current?.clientHeight || chartHeightConstants?.default;
        chartConfig.chart.height = calculateDynamicChartHeight(
            conditionTrendsData,
            true,
            containerHeight
        );

        const { yAxis, seriesData, allSeriesLength } = getSeries(conditionTrendsData, chartInstance, energyAnomalyKPI);

        // Build a set of anomaly timestamps (KpiCalculatedSignal value === 1) for tooltip detection
        const kpiCalculatedSignalData = conditionTrendsData?.find(item => item.type === CHART_CONDITION_TREND_KPI_CALCULATED_SIGNAL_TYPE);
        const anomalyTimestampSet = new Set(
            (kpiCalculatedSignalData?.values || [])
                .filter(p => p?.value === 1)
                .map(p => new Date(p.timestamp).getTime())
        );

        chartConfig.tooltip.formatter = function () {
            const isAnomaly = anomalyTimestampSet.has(this.x);
            return renderToStaticMarkup(
                <TrendTooltip
                    points={this.points?.filter(p =>
                        p?.series?.name !== CHART_CONDITION_TREND_KPI_TYPE &&
                        p?.series?.name !== CHART_CONDITION_TREND_KPI_CALCULATED_SIGNAL_TYPE
                    )}
                    timeStamp={this.x}
                    isUTC={false}
                    anomalyLevel={isAnomaly ? 1 : 0}
                />
            );
        };

        // Default navigator zoom: show last 2 days; preserve user zoom when navigator extremes are set
        const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;
        const kpiSignalData = conditionTrendsData?.find(item => item.type === CHART_CONDITION_TREND_KPI_SIGNAL_TYPE);
        const kpiStatusData = conditionTrendsData?.find(item => item.type === CHART_CONDITION_TREND_KPI_TYPE);
        const signalValues = kpiSignalData?.values?.length ? kpiSignalData.values : kpiStatusData?.values || [];
        const maxTs = signalValues.length
            ? new Date(signalValues[signalValues.length - 1]?.timestamp).getTime()
            : null;

        const defaultZoomRange = maxTs ? { min: maxTs - TWO_DAYS_MS, max: maxTs } : {};
        const xAxisExtremes = navigatorExtremes
            ? { min: navigatorExtremes?.min, max: navigatorExtremes?.max }
            : defaultZoomRange;

        chartConfig.xAxis = {
            ...chartConfig.xAxis,
            ...xAxisExtremes,
            events: {
                afterSetExtremes: (e) => {
                    if (e.trigger === 'navigator') {
                        setNavigatorExtremes({ min: e.min, max: e.max });
                    }
                }
            }
        };
        chartConfig.yAxis = yAxis;
        chartConfig.series = seriesData;
        chartConfig.plotOptions.series.boostThreshold =
            allSeriesLength > defaultBoostThreshold ? 1 : 0;

        setOptions({ ...chartConfig });
    }, [energyAnomalyKPI, conditionTrendsData, chartHeight]);

    return options;
};

export default useChartConfig;
